/**
 * Stateful hooks for InfrastructureMapCanvas.
 *
 * Extracted from InfrastructureMapCanvas.tsx so the orchestrating component
 * stays under the max-lines ceiling.
 */

import { useCallback, useEffect, useRef } from 'react'
import type {
  CanvasPosition,
  CanvasViewport,
  InfrastructureMapCanvasProps,
  TileSize,
} from '../../types/infrastructure-map'
import type { useInfrastructureMap } from '../../hooks/use-infrastructure-map'
import type { NodeContextMenuState } from './canvas'
import { computeFocalZoom } from './infrastructure-map-canvas.helpers'

type InfraMap = ReturnType<typeof useInfrastructureMap>

const ZOOM_OUT_FACTOR = 0.9
const ZOOM_IN_FACTOR = 1.1
const ZOOM_MIN = 0.25
const ZOOM_MAX = 3
const ZOOM_BUTTON_FACTOR = 1.25

// =============================================================================
// LIFE-CYCLE HOOKS
// =============================================================================

export function useContainerResizeObserver(
  containerRef: React.RefObject<HTMLDivElement | null>,
  setViewport: React.Dispatch<React.SetStateAction<CanvasViewport>>
): void {
  useEffect(() => {
    if (!containerRef.current) return
    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const { width, height } = entry.contentRect
      if (width > 0 && height > 0) {
        setViewport((prev) => ({ ...prev, width, height }))
      }
    })
    resizeObserver.observe(containerRef.current)
    return () => {
      resizeObserver.disconnect()
    }
  }, [containerRef, setViewport])
}

export function useInitialMapStyle(
  initialStyle: InfrastructureMapCanvasProps['initialStyle'],
  infraMap: InfraMap
): void {
  const hasAppliedRef = useRef(false)
  useEffect(() => {
    if (hasAppliedRef.current) return
    hasAppliedRef.current = true
    if (initialStyle && initialStyle !== infraMap.mapStyle) {
      infraMap.setMapStyle(initialStyle)
    }
  }, [initialStyle, infraMap])
}

export function useAutoCalculateLayout(infraMap: InfraMap): void {
  useEffect(() => {
    const hasItemsWithoutPositions = Array.from(infraMap.displayInfo.values()).some(
      (info) => (info.displayType === 'tile' || info.displayType === 'dot') && !info.position
    )
    if (
      hasItemsWithoutPositions &&
      !infraMap.layout.isCalculating &&
      infraMap.displayInfo.size > 0
    ) {
      void infraMap.recalculateLayout()
    }
  }, [infraMap])
}

// =============================================================================
// PAN / ZOOM HANDLERS
// =============================================================================

interface PanZoomHandlers {
  onPanStart: (e: React.MouseEvent) => void
  onPanMove: (e: React.MouseEvent) => void
  onPanEnd: () => void
  onWheel: (e: React.WheelEvent) => void
  zoomIn: () => void
  zoomOut: () => void
  resetZoom: () => void
}

export interface PanZoomState {
  viewport: CanvasViewport
  setViewport: React.Dispatch<React.SetStateAction<CanvasViewport>>
  isPanning: boolean
  setIsPanning: React.Dispatch<React.SetStateAction<boolean>>
  panStart: { x: number; y: number }
  setPanStart: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>
}

export interface PanZoomFocus {
  selectedEndpointIds: Set<string>
  displayInfo: InfraMap['displayInfo']
}

export function usePanZoom(state: PanZoomState, focus: PanZoomFocus): PanZoomHandlers {
  const { viewport, setViewport, isPanning, setIsPanning, panStart, setPanStart } = state
  const { selectedEndpointIds, displayInfo } = focus
  const onPanStart = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement
      if (target.closest('[data-node]')) return
      if (e.button === 0) {
        setIsPanning(true)
        setPanStart({ x: e.clientX - viewport.x, y: e.clientY - viewport.y })
      }
    },
    [viewport.x, viewport.y, setIsPanning, setPanStart]
  )

  const onPanMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isPanning) return
      setViewport((prev) => ({ ...prev, x: e.clientX - panStart.x, y: e.clientY - panStart.y }))
    },
    [isPanning, panStart, setViewport]
  )

  const onPanEnd = useCallback(() => {
    setIsPanning(false)
  }, [setIsPanning])

  const onWheel = useCallback(
    (e: React.WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return
      e.preventDefault()
      const delta = e.deltaY > 0 ? ZOOM_OUT_FACTOR : ZOOM_IN_FACTOR
      setViewport((prev) => ({
        ...prev,
        zoom: Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, prev.zoom * delta)),
      }))
    },
    [setViewport]
  )

  const zoomIn = useCallback(() => {
    setViewport((prev) =>
      computeFocalZoom({
        prev,
        newZoom: Math.min(ZOOM_MAX, prev.zoom * ZOOM_BUTTON_FACTOR),
        selectedEndpointIds,
        displayInfo,
      })
    )
  }, [selectedEndpointIds, displayInfo, setViewport])

  const zoomOut = useCallback(() => {
    setViewport((prev) =>
      computeFocalZoom({
        prev,
        newZoom: Math.max(ZOOM_MIN, prev.zoom / ZOOM_BUTTON_FACTOR),
        selectedEndpointIds,
        displayInfo,
      })
    )
  }, [selectedEndpointIds, displayInfo, setViewport])

  const resetZoom = useCallback(() => {
    setViewport((prev) => ({ ...prev, x: 0, y: 0, zoom: 1 }))
  }, [setViewport])

  return { onPanStart, onPanMove, onPanEnd, onWheel, zoomIn, zoomOut, resetZoom }
}

// =============================================================================
// NODE SELECTION
// =============================================================================

interface NodeSelectionHandlers {
  onNodeClick: (endpointId: string, e?: React.MouseEvent) => void
}

export function useNodeSelection(
  setSelectedEndpointIds: React.Dispatch<React.SetStateAction<Set<string>>>,
  lastClickedIdRef: React.MutableRefObject<string | null>,
  infraMap: InfraMap
): NodeSelectionHandlers {
  const onShiftClick = useCallback(
    (endpointId: string) => {
      if (!lastClickedIdRef.current) {
        setSelectedEndpointIds(new Set([endpointId]))
        lastClickedIdRef.current = endpointId
        return
      }
      const lastInfo = infraMap.displayInfo.get(lastClickedIdRef.current)
      const currentInfo = infraMap.displayInfo.get(endpointId)
      if (!lastInfo?.position || !currentInfo?.position) {
        setSelectedEndpointIds((prev) => new Set([...prev, endpointId]))
        return
      }
      const idsInRange = idsInBoundingBox(
        lastInfo.position,
        currentInfo.position,
        infraMap.displayInfo
      )
      setSelectedEndpointIds(new Set(idsInRange))
      lastClickedIdRef.current = endpointId
    },
    [infraMap.displayInfo, lastClickedIdRef, setSelectedEndpointIds]
  )

  const onCtrlClick = useCallback(
    (endpointId: string) => {
      setSelectedEndpointIds((prev) => {
        const next = new Set(prev)
        if (next.has(endpointId)) next.delete(endpointId)
        else next.add(endpointId)
        return next
      })
      lastClickedIdRef.current = endpointId
    },
    [lastClickedIdRef, setSelectedEndpointIds]
  )

  const onSingleClick = useCallback(
    (endpointId: string) => {
      if (infraMap.mapStyle === 'radial') {
        const currentRootId = infraMap.styleSettings.settings?.rootNodeId
        if (endpointId !== currentRootId) void infraMap.recalculateLayout(endpointId)
      }
      setSelectedEndpointIds(new Set([endpointId]))
      lastClickedIdRef.current = endpointId
    },
    [infraMap, lastClickedIdRef, setSelectedEndpointIds]
  )

  const onNodeClick = useCallback(
    (endpointId: string, e?: React.MouseEvent) => {
      if (infraMap.linkCreation.state.isActive) {
        if (!infraMap.linkCreation.state.parentEndpointId) {
          infraMap.linkCreation.selectParent(endpointId)
        } else {
          infraMap.linkCreation.toggleChild(endpointId)
        }
        return
      }
      if (e?.ctrlKey || e?.metaKey) {
        onCtrlClick(endpointId)
      } else if (e?.shiftKey && lastClickedIdRef.current) {
        onShiftClick(endpointId)
      } else {
        onSingleClick(endpointId)
      }
    },
    [infraMap.linkCreation, lastClickedIdRef, onCtrlClick, onShiftClick, onSingleClick]
  )

  return { onNodeClick }
}

function idsInBoundingBox(
  start: CanvasPosition,
  end: CanvasPosition,
  displayInfo: InfraMap['displayInfo']
): string[] {
  const minX = Math.min(start.x, end.x)
  const maxX = Math.max(start.x, end.x)
  const minY = Math.min(start.y, end.y)
  const maxY = Math.max(start.y, end.y)
  const ids: string[] = []
  for (const [id, info] of displayInfo.entries()) {
    if (!info.position) continue
    const { x, y } = info.position
    if (x >= minX && x <= maxX && y >= minY && y <= maxY) ids.push(id)
  }
  return ids
}

// =============================================================================
// NODE DRAG
// =============================================================================

interface NodeDragHandlers {
  onDrag: (endpointId: string, position: CanvasPosition) => void
  onDragEnd: (endpointId: string, position: CanvasPosition) => Promise<void>
}

export function useNodeDragHandlers(
  setDragPositions: React.Dispatch<React.SetStateAction<Map<string, CanvasPosition>>>,
  infraMap: InfraMap
): NodeDragHandlers {
  const onDrag = useCallback(
    (endpointId: string, position: CanvasPosition) => {
      setDragPositions((prev) => {
        const next = new Map(prev)
        next.set(endpointId, position)
        return next
      })
    },
    [setDragPositions]
  )

  const onDragEnd = useCallback(
    async (endpointId: string, position: CanvasPosition) => {
      setDragPositions((prev) => {
        const next = new Map(prev)
        next.delete(endpointId)
        return next
      })
      await infraMap.moveNode(endpointId, position)
    },
    [infraMap, setDragPositions]
  )

  return { onDrag, onDragEnd }
}

// =============================================================================
// CONTEXT-MENU ACTIONS
// =============================================================================

interface ContextMenuActions {
  onChangeTileSize: (size: TileSize) => void
  onAddToMapSet: () => void
  onRemoveFromMapSet: () => void
  onAddToCollectionSet: () => void
  onRemoveFromCollectionSet: () => void
  onAddSelectedToCollectionSet: () => void
  onViewDetails: () => void
}

export interface ContextMenuStateRefs {
  nodeContextMenu: NodeContextMenuState | null
  setNodeContextMenu: React.Dispatch<React.SetStateAction<NodeContextMenuState | null>>
  setDetailsEndpointId: React.Dispatch<React.SetStateAction<string | null>>
}

export interface CollectionSetActions {
  addToSet: (id: string) => void
  removeFromSet: (id: string) => void
  addMultiple: (ids: string[]) => void
}

export interface ContextMenuDeps {
  infraMap: InfraMap
  collectionSet: CollectionSetActions
  selectedEndpointIds: Set<string>
}

export function useContextMenuActions(
  state: ContextMenuStateRefs,
  deps: ContextMenuDeps
): ContextMenuActions {
  const { nodeContextMenu, setNodeContextMenu, setDetailsEndpointId } = state
  const { infraMap, collectionSet, selectedEndpointIds } = deps
  const { addToSet, removeFromSet, addMultiple } = collectionSet
  const onChangeTileSize = useCallback(
    (size: TileSize) => {
      if (!nodeContextMenu) return
      if (nodeContextMenu.currentSize === 'dot' && size !== 'dot') {
        infraMap.mapSet.add(nodeContextMenu.endpointId)
      }
      if (size === 'dot' && nodeContextMenu.currentSize !== 'dot') {
        infraMap.mapSet.remove(nodeContextMenu.endpointId)
      }
      void infraMap.resizeTile(nodeContextMenu.endpointId, size)
      setNodeContextMenu(null)
    },
    [nodeContextMenu, infraMap, setNodeContextMenu]
  )

  const onAddToMapSet = useCallback(() => {
    if (!nodeContextMenu) return
    infraMap.mapSet.add(nodeContextMenu.endpointId)
    setNodeContextMenu(null)
  }, [nodeContextMenu, infraMap.mapSet, setNodeContextMenu])

  const onRemoveFromMapSet = useCallback(() => {
    if (!nodeContextMenu) return
    infraMap.mapSet.remove(nodeContextMenu.endpointId)
    setNodeContextMenu(null)
  }, [nodeContextMenu, infraMap.mapSet, setNodeContextMenu])

  const onAddToCollectionSet = useCallback(() => {
    if (!nodeContextMenu) return
    addToSet(nodeContextMenu.endpointId)
    setNodeContextMenu(null)
  }, [nodeContextMenu, addToSet, setNodeContextMenu])

  const onRemoveFromCollectionSet = useCallback(() => {
    if (!nodeContextMenu) return
    removeFromSet(nodeContextMenu.endpointId)
    setNodeContextMenu(null)
  }, [nodeContextMenu, removeFromSet, setNodeContextMenu])

  const onAddSelectedToCollectionSet = useCallback(() => {
    if (selectedEndpointIds.size === 0) return
    addMultiple(Array.from(selectedEndpointIds))
    setNodeContextMenu(null)
  }, [selectedEndpointIds, addMultiple, setNodeContextMenu])

  const onViewDetails = useCallback(() => {
    if (!nodeContextMenu) return
    setDetailsEndpointId(nodeContextMenu.endpointId)
    setNodeContextMenu(null)
  }, [nodeContextMenu, setDetailsEndpointId, setNodeContextMenu])

  return {
    onChangeTileSize,
    onAddToMapSet,
    onRemoveFromMapSet,
    onAddToCollectionSet,
    onRemoveFromCollectionSet,
    onAddSelectedToCollectionSet,
    onViewDetails,
  }
}
